import store, { waitForUser } from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { ProductCardData } from '../../product-card/product-card-display';
import { reverseCatalogMap } from '../../../common/utils/util';
import { SearchFacet, SearchResult, ViewTypeLabel } from '../../search/interfaces/search.interface';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { customElement, state } from 'lit/decorators.js';
import { html, nothing } from 'lit';
import {
  isSuccess,
  selectFacets,
  selectPredefinedFilters,
  selectRequest,
  selectResults,
  selectSelectedCatalog,
  selectView
} from '../../search/store/search.selector';
import { mapProductCardData } from '../../search/mapping/search-product-card.mapping';
import { renderNoResultTemplate } from '../../search/utils/search-results.util';
import { isSuccessUser } from '../../user-profile/user-profile.selectors';
import { PredefinedFiltersObjects } from '../../search/interfaces/predefined-filters.interface';
import { cleanFilterValues, createPredefinedFiltersObjectList } from '../../search/utils/predefined-filter.util';
import { captureAnalyticsForPrintCatalogSearchListAndCardView } from '../analytics/print-catalog-search-results.analytics';
import emit from '../../../common/events';

@customElement('mhecomm-print-catalog-search-results')
export class PrintCatalogSearchResults extends connect(store)(BaseComponent) {
  /**
   * The product cards to display.
   */
  @state()
  private productCards: Array<ProductCardData> = [];

  /**
   * The search results retrieved from Redux.
   */
  @state()
  private results: Array<SearchResult> = [];

  /**
   * The view type to display the results.
   */
  @state()
  private view: ViewTypeLabel = ViewTypeLabel.LIST;

  /**
   * The catalog retrieved from the search request.
   */
  @state()
  private catalog?: string;

  /**
   * The selected catalog retrieved from the search state.
   */
  @state()
  private selectedCatalog?: string;

  /**
   * The current store retrieved from the search request.
   */
  @state()
  private store?: string;

  @state()
  private isSuccessUser = false;

  @state()
  private facets: SearchFacet[] = [];

  @state()
  private predefinedFiltersObjectList: PredefinedFiltersObjects[] = [];

  @state()
  private _isStateModified = false;

  predefinedFiltersList: string[] = [];

  stateChanged(state: StoreState): void {
    const request = selectRequest(state);
    this.catalog = reverseCatalogMap(request ? request.catalog : '');
    this.selectedCatalog = selectSelectedCatalog(state);
    this.store = request?.store;
    this.view = selectView(state) ?? ViewTypeLabel.LIST;
    this.isSuccessUser = isSuccessUser(state);

    const results = selectResults(state) || [];
    const facets = selectFacets(state) || [];
    const predefinedFilters = selectPredefinedFilters(state) || [];
    const predefinedFiltersArr = cleanFilterValues(predefinedFilters);
    const predefinedFiltersObjectList = createPredefinedFiltersObjectList(facets, predefinedFiltersArr);

    if (!this._isStateModified && predefinedFiltersObjectList.length > 0) {
      this._isStateModified = true;
      this.predefinedFiltersObjectList = predefinedFiltersObjectList;
    }
    
    if (this.results !== results) {
      this.results = results;
      this.facets = facets;
      this.productCards = mapProductCardData(this.results, this.catalog, this.selectedCatalog, this.store, true);
    }
  }

  _loadPriceBanner() {
    return (!this.isSuccessUser) 
      ? html `
        <mhecomm-school-pricing-alert></mhecomm-school-pricing-alert>` 
      : nothing;
  }

  renderComp() {
    return html`
      <div id="search-results" tabindex="-1" role="region" aria-label="Results">
        ${this._loadPriceBanner()}
        ${this.productCards?.length > 0
          ? this.getResults()
          : this.noResults()
        }
        <mhecomm-wish-list-update-product-off-canvas></mhecomm-wish-list-update-product-off-canvas>
      </div>
    `;
  }

  /**
   * Renders the LIST or CARD results based on the view type
   *
   * @private
   */
  private getResults() {
    if (this.predefinedFiltersObjectList.length > 0) {
      this.predefinedFiltersList = this.predefinedFiltersObjectList.map(filter => filter.name);
      emit(captureAnalyticsForPrintCatalogSearchListAndCardView(this.view, this.productCards!, this.predefinedFiltersObjectList, this.facets!, this.predefinedFiltersList));
    }
    if(this.view === ViewTypeLabel.LIST) {
      return html`
        <mhecomm-print-catalog-list-view 
          .products=${this.productCards}
          ?showProductWishList=${true}
        >
        </mhecomm-print-catalog-list-view>
      `;
    }
    return html`
      <mhecomm-print-catalog-card-view 
        .products=${this.productCards}
        ?showProductWishList=${true}
      >
      </mhecomm-print-catalog-card-view>
    `;
  }

  /**
   * Renders the no result template if the search has occurred, but no results were found.
   *
   * @private
   */
  private noResults() {
    return html`
      ${isSuccess(store.getState() as StoreState) ? renderNoResultTemplate() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-print-catalog-search-results': PrintCatalogSearchResults;
  }
}
